import React, { useState, useEffect, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import billingHistoryService from '../../services/billingHistoryService';
import type { AnnualBillingFilters, BillingHistoryResponse } from '../../types/billingHistory';
import { ConsolidatedBillingCard, CompanyBillingSection } from './billingShared';

// ─── Helpers ────────────────────────────────────────────────────

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 3; y <= currentYear + 1; y++) {
    years.push(y);
  }
  return years;
};

// ─── Main Component ─────────────────────────────────────────────

export const AnnualBillingView: React.FC = () => {
  const [filters, setFilters] = useState<AnnualBillingFilters>({
    year: new Date().getFullYear(),
  });
  const [billingData, setBillingData] = useState<BillingHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const billing = await billingHistoryService.getAnnualBilling(filters);
      setBillingData(billing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ano</label>
            <select
              value={filters.year}
              onChange={e => setFilters({ year: Number(e.target.value) })}
              className="block w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
            >
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && billingData && (
        <>
          {/* ─── Consolidated Summary ─────────────────────── */}
          <ConsolidatedBillingCard data={billingData} totalLabel={`Faturamento Total (${filters.year})`} />

          {/* ─── Per-Company Sections ─────────────────────── */}
          {billingData.companies.map(company => (
            <CompanyBillingSection key={company.companyId} company={company} />
          ))}
        </>
      )}
    </div>
  );
};

export default AnnualBillingView;
